/**
 * FormalRV's own tools, compiled to JavaScript by js_of_ocaml (formalrv-web/build.sh), run in the
 * browser. Each run gets a fresh Web Worker: the tools run their `main` when loaded and keep global
 * state, and a run that does not finish in time is simply terminated.
 */

export interface ToolRun {
  code: number
  stdout: string
  stderr: string
  files: Record<string, string>
  ms: number
  timedOut?: boolean
}

export interface BrowserManifest {
  formalrv_commit: string
  built_at: string
  ocaml: string
  tools: Record<string, { file: string; bytes: number; sha256: string }>
}

const BASE = '/formalrv/'
let manifest: Promise<BrowserManifest | null> | null = null

export function loadManifest(): Promise<BrowserManifest | null> {
  if (!manifest) {
    manifest = fetch(`${BASE}manifest.json`, { cache: 'no-cache' })
      .then(r => (r.ok ? r.json() : null))
      .catch(() => null)
  }
  return manifest
}

export async function runTool(
  tool: string,
  argv: string[],
  files: { name: string; text: string }[],
  { collect = [] as string[], timeoutMs = 30000, env = {} as Record<string, string> } = {},
): Promise<ToolRun> {
  const m = await loadManifest()
  const entry = m?.tools[tool]
  if (!entry) throw new Error(`${tool} is not available in this browser build`)
  const script = `${BASE}${entry.file}?v=${entry.sha256.slice(0, 12)}`
  return new Promise((resolve, reject) => {
    const worker = new Worker(`${BASE}runner.worker.js?v=${encodeURIComponent(m!.built_at)}`)
    const timer = window.setTimeout(() => {
      worker.terminate()
      resolve({ code: 124, stdout: '', stderr: `${tool} did not finish within ${timeoutMs / 1000} s`, files: {}, ms: timeoutMs, timedOut: true })
    }, timeoutMs)
    worker.onmessage = e => { window.clearTimeout(timer); worker.terminate(); resolve(e.data as ToolRun) }
    worker.onerror = e => { window.clearTimeout(timer); worker.terminate(); reject(new Error(e.message || `${tool} failed to load`)) }
    worker.postMessage({ tool, script, argv, files, collect, env })
  })
}

export interface LiveStep {
  from: string
  to: string
  command: string
  exit: number
  ms: number
  log: string
}

/** `params n, cst` → ['n', 'cst']. */
export function declaredParams(prog: string): string[] {
  const m = prog.match(/^\s*params\s+(.+)$/m)
  return m ? m[1].split(',').map(s => s.trim()).filter(Boolean) : []
}

/** OCaml's %S prints non-ASCII bytes as \ddd escapes ("\226\128\148" for "—"); show the characters. */
export function decodeOcamlEscapes(s: string): string {
  return s.replace(/(?:\\\d{3})+/g, run => {
    const bytes = run.match(/\\(\d{3})/g)!.map(e => Number(e.slice(1)))
    if (bytes.some(b => b > 255)) return run
    try { return new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(bytes)) } catch { return run }
  })
}

const logOf = (r: ToolRun) => decodeOcamlEscapes([r.stdout, r.stderr].filter(Boolean).join('\n'))

/** The code a source selects with its `code surface d=3` line; prepare_qec must be asked for that code. */
export function declaredCode(prog: string): { code: string; distance: number } | null {
  const m = prog.match(/^\s*code\s+([A-Za-z_]\w*)\s+d\s*=\s*(\d+)/m)
  return m ? { code: m[1], distance: Number(m[2]) } : null
}

/** .prog → .qppm → .qec with FormalRV's own front end, in the browser. The source is placed at its
 *  FormalRV path, beside the files its `use` lines import, so relative imports resolve as they do natively. */
export async function compileFront(prog: string, opts: {
  specialize: Record<string, number>; code: string; distance: number
  qecOpts?: string[]; sourcePath?: string; includes?: { path: string; text: string }[]
}) {
  const env = { FORMALRV_JOBS: '1' }
  const steps: LiveStep[] = []
  const texts: { qppm?: string; qec?: string } = {}
  const show = (argv: string[]) => argv.map(a => a.replace(/^\/static\//, '')).join(' ')
  const source = `/static/${opts.sourcePath ?? 'Main.prog'}`
  const files = [{ name: source, text: prog }, ...(opts.includes ?? []).map(f => ({ name: `/static/${f.path}`, text: f.text }))]

  const a1 = ['--source', source, '-o', '/static/Main.qppm']
  const r1 = await runTool('prepare_qppm', a1, files, { collect: ['/static/Main.qppm'], env })
  steps.push({ from: 'prog', to: 'qppm', command: `prepare_qppm ${show(a1)}`, exit: r1.code, ms: r1.ms, log: logOf(r1) })
  if (r1.code !== 0 || !r1.files['/static/Main.qppm']) return { steps, texts }
  texts.qppm = r1.files['/static/Main.qppm']

  const spec = Object.entries(opts.specialize).flatMap(([k, v]) => ['--specialize', `${k}=${v}`])
  // The source's own `code` line selects the code; asking prepare_qec for another is refused.
  const code = declaredCode(prog) ?? { code: opts.code, distance: opts.distance }
  const a2 = ['/static/Main.qppm', '--code', code.code, '--distance', String(code.distance), ...spec, ...(opts.qecOpts ?? []), '-o', '/static/Main.qec']
  const r2 = await runTool('prepare_qec', a2, [{ name: '/static/Main.qppm', text: texts.qppm }], { collect: ['/static/Main.qec'], env, timeoutMs: 60000 })
  steps.push({ from: 'qppm', to: 'qec', command: `prepare_qec ${show(a2)}`, exit: r2.code, ms: r2.ms, log: logOf(r2) })
  if (r2.code === 0 && r2.files['/static/Main.qec']) texts.qec = r2.files['/static/Main.qec']
  return { steps, texts }
}

/** The resource VM on one listing: its JSON verdict, as `resource_vm FILE --json` prints it. */
export async function runVm(sysprog: string, extraArgs: string[] = []) {
  const run = await runTool('resource_vm', ['/static/input.sysprog', '--json', ...extraArgs],
    [{ name: '/static/input.sysprog', text: sysprog }])
  let verdict: Record<string, unknown> | null = null
  try { verdict = JSON.parse(run.stdout) } catch { verdict = null }
  return { run, verdict }
}
